import { Filter } from '@cubejs-client/core';
import { useCubeQuery } from '@cubejs-client/react';
import { formatNumber, MonsterChartProps, NumberFormatPreset } from '@procureai/ui-mui';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SupplierSpendAnalysisSource } from '@/generated/gql/graphql';
import { I18nNamespace } from '~/common/types';
import { monthsInYearFormatter } from '~/components/CubeChart/CubeChart.common';
import { useModuleContext } from '~/components/ModuleContextProvider';
import { getSegments, lastYearDateRange } from '~/util/cube';

import { mergeDataSources } from './SpendAnalysisWidget.util';

export interface UseSpendAnalysisResult {
  title: string;
  subTitle: string;
  showAll: string;
  sources: {
    label: string;
    value: SupplierSpendAnalysisSource | null;
  }[];
  data: MonsterChartProps['data'];
  dataConfig: MonsterChartProps['dataConfig'];
  yAxis: MonsterChartProps['yAxis'];
  left: number;
  right: number;
  isLoading: boolean;
  setLeft: (index: number) => void;
  setRight: (index: number) => void;
}

export const useSpendAnalysis = (): UseSpendAnalysisResult => {
  const { entityId } = useModuleContext();
  const { t } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'SpendAnalysisWidget' });

  const [left, setLeft] = useState(0);
  const [right, setRight] = useState(1);

  const sources = useMemo(() => {
    return [
      {
        label: t('sources.orderCount'),
        value: SupplierSpendAnalysisSource.orderCount,
        tickFormatter: formatNumber(NumberFormatPreset.INTEGER_COMPACT),
      },
      {
        label: t('sources.orderVolume'),
        value: SupplierSpendAnalysisSource.orderVolume,
        tickFormatter: formatNumber(NumberFormatPreset.CURRENCY_COMPACT),
      },
    ];
  }, [t]);

  const yAxis = useMemo(() => {
    return {
      bars: {
        label: sources[left].label,
        tickFormatter: sources[left].tickFormatter,
        width: 85,
        padding: {
          top: 5,
        },
      },
      lines: {
        label: sources[right].label,
        tickFormatter: sources[right].tickFormatter,
        width: 85,
        padding: {
          top: 5,
        },
      },
    };
  }, [left, right, sources]);

  const cube = 'MonthlyOrderFacts';

  const filters: Filter[] = [
    {
      member: `${cube}.supplierId`,
      operator: 'equals',
      values: [entityId],
    },
  ];

  const monthlyOrderSeries = useCubeQuery({
    measures: [`${cube}.monthly_placed_orders`, `${cube}.monthly_order_volume`],
    filters,
    timeDimensions: [
      {
        dimension: `${cube}.date`,
        granularity: 'month',
        dateRange: lastYearDateRange,
      },
    ],
    order: {
      [`${cube}.date`]: 'asc',
    },
  });

  const chartData = useMemo(() => {
    const segmentsOrder = {
      [SupplierSpendAnalysisSource.orderCount]: getSegments(monthlyOrderSeries).segments,
      [SupplierSpendAnalysisSource.orderVolume]: getSegments(monthlyOrderSeries, 1).segments,
    };
    const leftKey = sources[left].value as keyof typeof segmentsOrder;
    const rightKey = sources[right].value as keyof typeof segmentsOrder;

    const leftSegments =
      segmentsOrder[leftKey]?.map((segment) => ({
        key: monthsInYearFormatter(new Date(segment.x)),
        data: [segment.value],
      })) || [];

    const rightSegments =
      segmentsOrder[rightKey]?.map((segment) => ({
        key: monthsInYearFormatter(new Date(segment.x)),
        data: [segment.value],
      })) || [];

    const spendAnalysisLeft = {
      id: '',
      title: '',
      labels: [sources[left].label],
      segments: leftSegments,
    };
    const spendAnalysisRight = {
      id: '',
      title: '',
      labels: [sources[right].label],
      segments: rightSegments,
    };
    return mergeDataSources(spendAnalysisLeft, spendAnalysisRight);
  }, [left, right, monthlyOrderSeries, sources]);

  const isLoading = monthlyOrderSeries.isLoading;

  return {
    title: t('title'),
    subTitle: t('subTitle'),
    showAll: t('showAll'),
    sources,
    left,
    right,
    setLeft,
    setRight,
    yAxis,
    isLoading,
    ...chartData,
  };
};
